import React from 'react';
import { MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { LOCAL_PAGES } from '../data/localPages';

/**
 * Local SEO section: semantic listing of intervention zones around Royan.
 * Hyper-targeted for local Google ranking on "nettoyage [commune]" queries.
 */
const ZonesIntervention = () => {
  const zonesPrincipales = [
    'Royan', 'Saint-Palais-sur-Mer', 'Vaux-sur-Mer',
    'Saint-Georges-de-Didonne', 'La Tremblade', 'Chaillevette',
  ];
  const zonesEtendues = [
    'Marennes', 'Saujon', 'Cozes', 'Médis', 'Mornac-sur-Seudre',
    'Étaules', 'Arvert', 'Les Mathes', 'La Palmyre',
    'Saint-Augustin', 'Breuillet', 'Saint-Sulpice-de-Royan',
    'Le Gua', 'Semussac', 'Meschers-sur-Gironde',
    'Boutenac-Touvent', 'Talmont-sur-Gironde', 'Brie-sous-Mortagne',
  ];

  return (
    <section
      id="zones"
      className="section-padding bg-gradient-to-br from-slate-50 to-blue-50/40 relative overflow-hidden"
      data-testid="zones-intervention-section"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-40 -translate-y-1/2 translate-x-1/2" />

      <div className="max-w-6xl mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            <MapPin className="w-4 h-4" /> Zones d'intervention
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nettoyage de vitres et ménage à Royan{' '}
            <span className="text-gradient">& alentours</span>
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            Basée à Chaillevette, notre équipe intervient dans toute la <strong>Charente-Maritime</strong> dans un rayon de <strong>25 km autour de Royan</strong>. Particuliers, commerces, bureaux, locations saisonnières — découvrez si votre commune est couverte.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Zones principales */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 sm:p-8 shadow-md">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[hsl(207,90%,54%)]" />
              Communes d'intervention prioritaire
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Délai d'intervention 48-72h, déplacement inclus dans le devis.
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {zonesPrincipales.map((z) => {
                // If a dedicated local-SEO page exists for "Nettoyage vitres <ville>",
                // turn the chip into a Link to that page for internal SEO meshing.
                const slug = `nettoyage-vitres-${z.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
                const hasPage = !!LOCAL_PAGES[slug];
                if (hasPage) {
                  return (
                    <Link
                      key={z}
                      to={`/${slug}`}
                      className="inline-flex items-center bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,30%)] hover:bg-[hsl(207,90%,54%)] hover:text-white px-3 py-1.5 rounded-full text-sm font-semibold border border-[hsl(207,90%,54%)]/20 transition-colors"
                      data-testid={`zone-principale-link-${slug}`}
                    >
                      {z} →
                    </Link>
                  );
                }
                return (
                  <span
                    key={z}
                    className="inline-flex items-center bg-[hsl(207,90%,54%)]/10 text-[hsl(207,90%,30%)] px-3 py-1.5 rounded-full text-sm font-semibold border border-[hsl(207,90%,54%)]/20"
                    data-testid={`zone-principale-${z.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {z}
                  </span>
                );
              })}
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[hsl(32,95%,55%)]" />
              Toute la presqu'île & Côte de Beauté
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              Intervention sous 5-8 jours, sans supplément kilométrique.
            </p>
            <div className="flex flex-wrap gap-2">
              {zonesEtendues.map((z) => (
                <span
                  key={z}
                  className="inline-flex items-center bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full text-sm"
                >
                  {z}
                </span>
              ))}
            </div>
          </div>

          {/* Sidebar CTA */}
          <div className="bg-gradient-to-br from-[hsl(207,90%,54%)] to-[hsl(207,90%,40%)] rounded-2xl p-6 sm:p-8 text-white shadow-xl flex flex-col">
            <MapPin className="w-10 h-10 mb-4 opacity-80" />
            <h3 className="text-2xl font-bold mb-3">Votre commune n'est pas listée ?</h3>
            <p className="opacity-90 mb-6 text-sm leading-relaxed">
              Nous étudions chaque demande au cas par cas. Pour les communes hors zone, un petit forfait kilométrique peut s'ajouter mais n'est pas un frein — appelez-nous.
            </p>
            <div className="space-y-3 mt-auto">
              <Button
                asChild
                className="bg-white text-[hsl(207,90%,40%)] hover:bg-blue-50 w-full font-bold"
                data-testid="zones-cta-call"
              >
                <a href="tel:+33625684849" className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> 06 25 68 48 49
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[hsl(207,90%,40%)] w-full font-semibold"
                data-testid="zones-cta-devis"
              >
                <a href="#reservation">Demander un devis</a>
              </Button>
            </div>
          </div>
        </div>

        {/* Internal linking block — sends SEO juice to service-specific blog posts */}
        <div className="mt-12 text-center text-sm text-gray-600 max-w-3xl mx-auto leading-relaxed">
          <p>
            Découvrez nos guides spécialisés par ville :{' '}
            <a href="/blog/nettoyage-vitres-royan-prix-delais-zones-intervention" className="text-[hsl(207,90%,44%)] font-semibold hover:underline">nettoyage de vitres à Royan</a>,{' '}
            <a href="/blog/nettoyage-canape-la-tremblade-taches-odeurs" className="text-[hsl(207,90%,44%)] font-semibold hover:underline">nettoyage de canapé à La Tremblade</a> et{' '}
            <a href="/blog/nettoyage-terrasse-saint-palais-sur-mer" className="text-[hsl(207,90%,44%)] font-semibold hover:underline">nettoyage de terrasse à Saint-Palais-sur-Mer</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ZonesIntervention;
